import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  SelfImprovement as WellBeingIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  IconButton,
  Breadcrumbs,
  Link,
  CircularProgress,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import MonthPickerCalendar from "components/common/MonthPickerCalendar";
import { DailyWellBeingScoreChart } from "components/features/patient/detail";
import { WellBeingScore } from "components/features/patient/detail/DailyWellBeingScoreChart";
import { DashboardLayout } from "components/layouts";
import { EPath } from "constants/path";
import {
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoHealthProviderCodeEnum,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
  MetricValuesAggregationRequestDtoOrderByEnum,
  MetricValuesAggregationResponseDto,
} from "libs/cellera-sdk";
import { activityApi } from "apis";
import { isEmpty, isNil } from "lodash";
import {
  endOfTheMonth,
  formatDate,
  startOfTheMonth,
} from "helpers/dateTimeHelper";

const DailyWellBeingHistoryPage: React.FC = () => {
  const { patientId, lastMonthChecked, lastYearChecked } = useParams<{
    patientId: string;
    lastMonthChecked?: string;
    lastYearChecked?: string;
  }>();
  const navigate = useNavigate();

  const initialMonth = useMemo(() => {
    if (lastMonthChecked && !Number.isNaN(Number(lastMonthChecked))) {
      return Number(lastMonthChecked);
    }
    return new Date().getMonth();
  }, [lastMonthChecked]);

  const initialYear = useMemo(() => {
    if (lastYearChecked && !Number.isNaN(Number(lastYearChecked))) {
      return Number(lastYearChecked);
    }
    return new Date().getFullYear();
  }, [lastYearChecked]);

  const [selectedMonth, setSelectedMonth] = useState<number>(initialMonth);
  const [selectedYear, setSelectedYear] = useState<number>(initialYear);
  const [startDate, setStartDate] = useState<string>(
    startOfTheMonth(initialYear, initialMonth).toISOString(),
  );
  const [endDate, setEndDate] = useState<string>(
    endOfTheMonth(initialYear, initialMonth).toISOString(),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<WellBeingScore[]>([]);

  const handleDateChange = useCallback((month: number, year: number) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    setStartDate(startOfTheMonth(year, month).toISOString());
    setEndDate(endOfTheMonth(year, month).toISOString());
  }, []);

  const fetchData = useCallback(
    async (
      targetPatientId: string | undefined,
      start: string,
      end: string,
    ): Promise<MetricValuesAggregationResponseDto> => {
      if (isNil(targetPatientId) || isEmpty(targetPatientId)) {
        return { data: [], totalIntervals: 0 };
      }

      return activityApi.getMetricValuesAggregatedByPatientId({
        patientId: targetPatientId,
        metricValuesAggregationRequestDto: {
          interval: MetricValuesAggregationRequestDtoIntervalEnum.Daily,
          aggregateTypes: [
            MetricValuesAggregationRequestDtoAggregateTypesEnum.LatestRecord,
          ],
          metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.WellBeing,
          healthProviderCode:
            MetricValuesAggregationRequestDtoHealthProviderCodeEnum.GoogleHealthConnect,
          orderBy: MetricValuesAggregationRequestDtoOrderByEnum.Desc,
          startDate: start,
          endDate: end,
        },
      });
    },
    [],
  );

  const loadData = useCallback(async () => {
    if (!patientId) return;
    setIsLoading(true);
    try {
      const response = await fetchData(patientId, startDate, endDate);
      setData(
        response.data.map((item) => ({
          date: item.startDate,
          value: item.valueIntData?.latest ?? 0,
          formattedDate: formatDate(item.startDate, "MMM DD, YYYY"),
        })),
      );
    } finally {
      setIsLoading(false);
    }
  }, [patientId, startDate, endDate, fetchData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = useCallback(() => {
    void loadData();
  }, [loadData]);

  if (!patientId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>No patient found</Typography>
      </Box>
    );
  }

  return (
    <DashboardLayout title="Daily Well Being Score">
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Breadcrumbs>
            <Link
              color="inherit"
              component="button"
              onClick={() => navigate(EPath.HOME)}
            >
              Dashboard
            </Link>
            <Link
              color="inherit"
              component="button"
              onClick={() => navigate(EPath.PATIENT_MANAGEMENT)}
            >
              Patient Management
            </Link>
            <Link
              color="inherit"
              component="button"
              onClick={() =>
                navigate(EPath.PATIENT_DETAIL.replace(":patientId", patientId))
              }
            >
              Patient Details
            </Link>
            <Typography color="text.primary">Daily Well Being Score</Typography>
          </Breadcrumbs>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MonthPickerCalendar
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              onDateChange={handleDateChange}
            />
            <IconButton
              onClick={() => navigate(-1)}
              size="small"
              sx={{ color: "text.secondary" }}
              title="Back"
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              onClick={handleRefresh}
              size="small"
              sx={{ color: "text.secondary" }}
              title="Refresh"
            >
              {isLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          <WellBeingIcon color="primary" />
          <Typography variant="h6" color="text.primary">
            Daily Well Being Score
          </Typography>
        </Box>

        <DailyWellBeingScoreChart data={data} isLoading={isLoading} />
      </Box>
    </DashboardLayout>
  );
};

export default DailyWellBeingHistoryPage;
